import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, PhoneCall } from 'lucide-react';

const FeatureCard = ({ Icon, title, description }) => (
  <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg">
    <Icon className="w-6 h-6 text-[#F49611] mb-4" />
    <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
    <p className="text-white/80 text-sm">{description}</p>
  </div>
);



// Hero Section
const HeroSection = () => {
  const dummy = "https://jamaappprod.s3.ap-south-1.amazonaws.com/Website/maxwealthweb/Web-Images+compressed/Offshore.png";
  const navigate = useNavigate();

  const handleBackClick = () => {
    // Navigate back to main page
    navigate('/wealth-services/offshore-products');

    // Use a small timeout to ensure navigation completes before trying to open dropdown
    setTimeout(() => {
      // Custom event to communicate with navbar component
      const event = new CustomEvent('openNavDropdown', {
        detail: {
          section: 'wealthManagement', // The Services section
          subsection: 0  // Index of "Investment Advisory" in the items array
        }
      });
      window.dispatchEvent(event);
    }, 100);
  };

  return (
    <div className="relative bg-gradient-to-br from-[#113262] to-[#113262] isolate min-h-[75vh] flex items-center">
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 lg:py-24">
        <div className="flex flex-col md:flex-row items-center justify-between">
          {/* Left content */}
          <div className="w-full md:w-1/2 lg:w-3/5 text-white space-y-5 md:space-y-6 md:pr-8 pt-8 md:pt-4">
            <button
              onClick={handleBackClick}
              className="flex items-center gap-2 text-orange-400 hover:text-orange-600 transition-all duration-300 font-medium text-sm mb-6 opacity-60 hover:opacity-100"
            >
              <ArrowLeft className="w-4 h-4 transition-transform duration-300 hover:translate-x-0.5" />
              Back to Wealth Management
            </button>


            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#113262] mb-4 leading-tight bg-gradient-to-r from-white to-[#A2B8D8] bg-clip-text text-transparent animate-slideUp py-2">
              Offshore Products
            </h1>

            <p className="text-lg md:text-xl text-[#A2B8D8] animate-fadeIn opacity-0" style={{ animationDelay: '0.3s' }}>
              Expand Your Investment Horizon
            </p>

            <p className="text-[#E8EEF6] text-sm md:text-base leading-relaxed animate-fadeIn opacity-0" style={{ animationDelay: '0.6s' }}>
              Diversify your portfolio with dollar-denominated investments across US equities, emerging markets, and global bonds through regulated investment vehicles.
            </p>

            {/* Button container in a row */}
            <div className="flex flex-row gap-4 animate-fadeIn opacity-0 mt-6 md:mt-8" style={{ animationDelay: '0.9s' }}>
              <a
                href="https://club.maxiomwealth.com/login"
                className="bg-[#F49611] text-white px-8 py-4 rounded-lg hover:transform hover:-translate-y-1 hover:shadow-lg transition-all duration-300 font-semibold"
              >
                Get Started
              </a>
              <a href="/meeting"
                className="border-2 border-[#A2B8D8] text-white px-5 py-2.5 md:px-6 md:py-3 rounded-lg hover:bg-white/10 transition-colors duration-300 flex items-center gap-2 font-semibold text-sm"
              >
                <PhoneCall size={18} className="shrink-0" />
                Schedule Meeting
              </a>
            </div>
          </div>

          {/* Right image - reduced vertical spacing on mobile */}
          <div className="w-full md:w-1/2 lg:w-2/5 pt-8 md:pt-20 mt-6 md:mt-12 md:mt-0 flex justify-center md:justify-end animate-fadeIn opacity-0" style={{ animationDelay: '1.2s' }}>
            <div className="relative w-full max-w-md px-4 md:px-0">
              <div className="absolute inset-0 rounded-full"></div>
              <img
                src={dummy}
                alt="Spark PMS visualization"
                className="relative z-10 w-full h-auto rounded-lg object-contain max-w-full scale-120"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const IntroSection = () => {
  const marketStats = [
    { label: "US Market Access", value: "60%", subtext: "of global market cap" },
    { label: "Emerging Markets", value: "30+", subtext: "countries coverage" },
    { label: "Global Bonds", value: "$128T", subtext: "market size" }
  ];

  return (
    <div className="bg-white">
      <div className="w-full max-w-6xl mx-auto p-6">
        <div className="grid md:grid-cols-2 gap-16">
          {/* Left Column */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-12 h-0.5 bg-[#F49611]"></div>
              <span className="text-[#F49611] font-medium uppercase tracking-wider text-sm">Why Offshore Investments</span>
            </div>
            <h2 className="text-4xl font-bold text-[#113262] mb-6">
              Expand Your Investment Horizon
            </h2>
            <p className="text-gray-600 mb-8">
              Our offshore investment solutions provide access to international markets through regulated investment vehicles. Benefit from geographic diversification while maintaining compliance with Indian regulations through GIFT City AIFs and Singapore jurisdiction funds.
            </p>
            <div className="space-y-4">
              {[
                "Access to US equities and emerging markets",
                "Dollar-denominated investment opportunities",
                "Regulated investment structures",
                "Professional portfolio management"
              ].map((point, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#F49611]"></div>
                  <span className="text-gray-700">{point}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column - Stats */}
          <div className="bg-[#E8EEF6] rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl font-bold text-[#113262] mb-8">Global Market Opportunities</h3>
            <div className="grid gap-8">
              {marketStats.map((stat, index) => (
                <div key={index} className="flex items-center gap-6">
                  <div className="w-24 h-24 bg-white rounded-lg flex items-center justify-center">
                    <span className="text-3xl font-bold text-[#F49611]">{stat.value}</span>
                  </div>
                  <div>
                    <div className="font-semibold text-[#113262]">{stat.label}</div>
                    <div className="text-gray-600">{stat.subtext}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const OffshoreProductsIntro = () => {
  return (
    <>
      <HeroSection />
      <IntroSection />
    </>
  );
};

export default OffshoreProductsIntro;